/**
 * @fileoverview Types per User e Authentication - Interfacce centrali
 *
 * Definisce tutte le interfacce e tipi relativi agli utenti e autenticazione
 * utilizzati in tutta l'applicazione. Centralizza i tipi per garantire
 * consistenza e riusabilità.
 *
 * @author Generated by TypeScript Migration
 * @since 2025-09-25
 */

import { ApplicationUserDto } from '../models/auth';
import { Gender } from '../models/worker';

/**
 * Interfaccia per le opzioni utente nei selector/dropdown
 *
 * Utilizzata nei componenti di selezione utenti per definire
 * opzioni disponibili e restrizioni di accesso.
 */
export interface UserRoleOption {
	/** Valore dell'opzione */
	value: string;

	/** Etichetta visualizzata */
	label: string;

	/** Ruoli che possono vedere/selezionare questa opzione */
	restrictedTo: readonly string[];
}

/**
 * Interfaccia per i dati di autenticazione
 */
export interface AuthCredentials {
	/** Email o username */
	username: string;

	/** Password */
	password: string;

	/** Remember me flag */
	remember?: boolean;
}

/**
 * Interfaccia per i token di autenticazione
 */
export interface AuthTokens {
	/** Token di accesso JWT */
	accessToken: string;

	/** Token di refresh JWT */
	refreshToken: string;

	/** Tipo del token (normalmente "Bearer") */
	tokenType?: string;

	/** Scadenza del token in secondi */
	expiresIn?: number;
}

/**
 * Interfaccia per lo stato di autenticazione completo
 */
export interface AuthState {
	/** Token di accesso JWT */
	accessToken: string | null;

	/** Token di refresh JWT */
	refreshToken: string | null;

	/** Informazioni utente corrente */
	userInfo: ApplicationUserDto | null;

	/** Informazioni utente impersonificato (se applicabile) */
	impersonatedUserInfo: ApplicationUserDto | null;

	/** Se l'utente è autenticato */
	isAuthenticated: boolean;

	/** Loading state durante autenticazione */
	isLoading: boolean;
}

/**
 * Type guards per validazione runtime degli utenti
 */
export const UserValidators = {
	/**
	 * Verifica se un utente ha un determinato ruolo
	 */
	hasRole(user: ApplicationUserDto | null, role: string): boolean {
		return user?.roles?.includes(role) ?? false;
	},

	/**
	 * Verifica se un utente ha almeno uno dei ruoli specificati
	 */
	hasAnyRole(user: ApplicationUserDto | null, roles: string[]): boolean {
		if (!user?.roles) {
			return false;
		}
		return roles.some((role) => user.roles.includes(role));
	},

	/**
	 * Verifica se un utente ha tutti i ruoli specificati
	 */
	hasAllRoles(user: ApplicationUserDto | null, roles: string[]): boolean {
		if (!user?.roles) {
			return false;
		}
		return roles.every((role) => user.roles.includes(role));
	},
} as const;

/**
 * Utility per generare avatar URL basato su ApplicationUserDto
 */
export const AvatarUtils = {
	/**
	 * Genera URL avatar per un utente
	 */
	getAvatarUrl(user: ApplicationUserDto | null): string {
		if (!user) {
			return 'https://eu.ui-avatars.com/api/?rounded=true&font-size=0.33&background=0D8ABC&color=fff&name=x';
		}

		if (user.avatarImage) {
			return user.avatarImage;
		}

		// Determina colori basati su genere
		let background = 'background=f4f4f5'; // Default grigio
		if (user.gender === Gender.Male) {
			background = 'background=0D8ABC&color=fff'; // Blu
		} else if (user.gender === Gender.Female) {
			background = 'background=FFC0CB&color=fff'; // Rosa
		}

		// Encode nome per URL safety
		const safeName = encodeURIComponent(user.name || 'User');

		return `https://eu.ui-avatars.com/api/?rounded=true&font-size=0.33&${background}&name=${safeName}`;
	},

	/**
	 * Genera iniziali per un utente
	 */
	getUserInitials(user: ApplicationUserDto | null): string {
		if (!user?.name) {
			return 'U';
		}

		const nameParts = user.name.trim().split(' ');
		if (nameParts.length >= 2) {
			return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
		}

		return user.name.substring(0, 2).toUpperCase();
	},
} as const;
